import { Bot, CheckCircle, Send } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { MessageBubble } from '../screening/message-bubble';

import { Button } from '~/components/ui/button';

interface InterviewPreviewProps {
  body: string;
  variant: 'intro' | 'outro';
}

export function InterviewPreview({ body, variant }: InterviewPreviewProps): React.ReactNode {
  const { t } = useTranslation();

  return (
    <div className="w-[360px] overflow-hidden rounded-xl border border-border bg-background shadow-sm">
      {/* Header */}
      <div className="border-b border-border bg-card px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/10">
            <Bot className="size-5 text-primary" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium">{t('screening.aiDisclosure')}</p>
            <p className="truncate text-xs text-muted-foreground">
              {t('screening.previewJobTitle')}
            </p>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex flex-col gap-3 px-4 py-4" style={{ minHeight: '200px' }}>
        {variant === 'outro' && (
          <>
            <MessageBubble
              role="assistant"
              content={t('communications.preview.outroSampleIntro')}
            />
            <MessageBubble
              role="candidate"
              content={t('communications.preview.outroSampleAnswer')}
            />
          </>
        )}
        <MessageBubble
          role="assistant"
          content={body || t(`communications.${variant}.scriptPlaceholder`)}
        />
        {variant === 'outro' && (
          <div className="pt-1">
            <Button className="w-full" disabled>
              <CheckCircle className="size-4" />
              {t('screening.submitInterview')}
            </Button>
          </div>
        )}
      </div>

      {/* Input bar */}
      <div className="border-t border-border bg-card px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="flex-1 rounded-xl border border-input bg-background px-4 py-2.5 text-sm text-muted-foreground opacity-50">
            {t('screening.typeMessage')}
          </div>
          <Button size="icon" className="size-11 shrink-0 rounded-xl opacity-50" disabled>
            <Send className="size-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
